import { useStableAuth } from './useStableAuth';

/**
 * Hook centralizado para obter o company_id do usuário logado.
 * 
 * IMPORTANTE: Sempre use este hook em vez de fazer queries diretas
 * na tabela profiles. Isso garante:
 * - Consistência em toda a aplicação
 * - Query correta com user_id (não id)
 * - Memoização para evitar re-renders
 * 
 * @example
 * const { companyId, isLoading, requireCompanyId } = useCurrentCompanyId();
 * 
 * // Uso básico
 * if (isLoading) return <Loading />;
 * if (!companyId) return <NoCompanyError />;
 * 
 * // Uso em handlers (lança erro se não disponível)
 * const handleSubmit = () => {
 *   const id = requireCompanyId();
 *   // ... usar id com segurança
 * };
 */
export const useCurrentCompanyId = () => {
  const { 
    companyId, 
    userId, 
    loading, 
    isAuthenticated,
    company,
    userRole,
    profile
  } = useStableAuth();

  /**
   * Retorna o companyId ou lança erro se não disponível
   * Use em funções que requerem company_id obrigatoriamente
   */
  const requireCompanyId = (): string => {
    if (loading) throw new Error('Context still loading');
    if (!companyId) throw new Error('Company ID not available');
    return companyId;
  };

  /**
   * Retorna o userId ou lança erro se não disponível
   */
  const requireUserId = (): string => {
    if (loading) throw new Error('Context still loading');
    if (!userId) throw new Error('User ID not available');
    return userId;
  };

  return {
    // IDs principais
    companyId,
    userId,
    
    // Estados
    isLoading: loading,
    isReady: !loading && !!companyId && !!userId,
    isAuthenticated,
    
    // Dados adicionais do contexto
    company,
    userRole,
    profile,
    
    // Funções auxiliares
    requireCompanyId,
    requireUserId,
  };
};

export type UseCurrentCompanyIdReturn = ReturnType<typeof useCurrentCompanyId>;
